import React from 'react';
import PropTypes from 'prop-types';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  type ApolloCache,
  type ApolloClientOptions,
  type NormalizedCacheObject,
} from '@apollo/client';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import {
  RetryLink,
  type RetryLink as RetryLinkType,
} from '@apollo/client/link/retry';
import { onError as createErrorLink } from '@apollo/client/link/error';
import { Sha256 } from '@aws-crypto/sha256-browser';
import { toHex } from '@smithy/util-hex-encoding';
import { datadogRum } from '@datadog/browser-rum';
import algoRequestIdTracker from '../algoRequestIdLink';
import logWeaselLink from '../logWeaselLink';
import costReportingLink, { includeFieldCost } from '../costReportingLink';

const sha256 = async (input: string): Promise<string> => {
  const hash = new Sha256();

  hash.update(input);

  return toHex(await hash.digest());
};

const getWindow = (): Window | undefined =>
  typeof window === 'object' ? window : undefined;

const defaultTLD = 'com';

// Return top-level domain (TLD) for the current URL. E.g. 'com' or 'co.uk'
const getCurrentTLD = () => {
  const win = getWindow();

  if (!win) return defaultTLD;

  const match = win.location.hostname.match(/stitchfix\.(.+)$/);

  return match ? match[1] : defaultTLD;
};

const getGraphqlURI = () => {
  const tld = getCurrentTLD();

  return `https://www.stitchfix.${tld}/graphql-api/api/graphql`;
};

const getDefaultOptions = ():
  | ApolloClientOptions<unknown>['defaultOptions']
  | undefined => {
  const win = getWindow();

  // When the `ApolloProvider` is rendered server-side, we aren't actually
  // using it to make queries, but it still caches things and leaks memory
  // (https://stitchfix.atlassian.net/browse/FT-512). Detailed explanation:
  // https://github.com/apollographql/apollo-client/issues/7942#issuecomment-914630190
  return !win ? { watchQuery: { fetchPolicy: 'no-cache' } } : undefined;
};

/**
 * Creates a GraphQL API provider for use in the browser.
 */
type OnError = (
  error: Error,
  args: { metadata: Record<string, unknown> },
) => void;

interface LocalOptions<
  TCacheShape extends NormalizedCacheObject = NormalizedCacheObject,
> {
  bearerToken?: string | null;
  cache?: ApolloCache<TCacheShape>;
  csrfToken?: string | null;
  defaultOptions?: ApolloClientOptions<TCacheShape>['defaultOptions'];
  graphqlURI?: string;
  name?: string;
  onError?: OnError;
  retry?: RetryLinkType.Options;
  shadowGraphqlURI?: string | null;
  typeDefs?: ApolloClientOptions<TCacheShape>['typeDefs'];
  useGETForHashedQueries?: boolean;
  useGETForQueries?: boolean;
  version?: string;
}

interface LocalReturn<
  TCacheShape extends NormalizedCacheObject = NormalizedCacheObject,
> {
  GraphQLApiProvider: (props: { children: React.ReactNode }) => JSX.Element;
  client: ApolloClient<TCacheShape>;
}

export const createGraphQLApiProvider = <
  TCacheShape extends NormalizedCacheObject = NormalizedCacheObject,
>({
  graphqlURI = getGraphqlURI(),
  shadowGraphqlURI = null,
  csrfToken = null,
  bearerToken = null,
  name,
  version,
  retry = {
    attempts: {
      max: 2,
    },
  },
  useGETForQueries = false,
  useGETForHashedQueries = false,
  typeDefs,
  cache,
  defaultOptions = getDefaultOptions(),
  onError = (
    error: Error,
    { metadata }: { metadata: Record<string, unknown> },
  ) => {
    datadogRum.addError(error, metadata);
  },
}: LocalOptions<TCacheShape>): LocalReturn<TCacheShape> => {
  if (!(name || version)) {
    throw new Error(
      `name: ${name} and version: ${version} are required for graphql provider initialization.`,
    );
  }

  const customFetch: typeof fetch = (uri, options) => {
    // This allows us to quietly issue requests to another
    // GraphQL endpoint for testing purposes. This should not impact
    // the client experience, because this fetches request isn't returned
    if (shadowGraphqlURI) {
      fetch(shadowGraphqlURI, options);
    }

    return fetch(uri, options);
  };

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (bearerToken) {
    headers.Authorization = `Bearer ${bearerToken}`;
  }

  if (csrfToken && !bearerToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  if (includeFieldCost()) {
    headers['X-GraphQL-Cost-Include-Fields'] = 'true';
  }

  const httpLink = createHttpLink({
    useGETForQueries,
    uri: operation => {
      if (!useGETForHashedQueries && operation.operationName) {
        // Add operation name to the query string for clearer observability
        return `${graphqlURI}?o=${operation.operationName}`;
      }

      return graphqlURI;
    },
    credentials: 'include',
    headers,
    fetch: customFetch,
  });

  const graphQLErrorLink = createErrorLink(({ graphQLErrors, operation }) => {
    if (!graphQLErrors) return;

    for (const e of graphQLErrors) {
      const error = new Error(`GraphQL error occurred: ${JSON.stringify(e)}`);

      if (operation.operationName) {
        error.name = `${operation.operationName}Error`;
      }

      onError(error, {
        metadata: {
          query: operation.query,
          operationName: operation.operationName,
        },
      });
    }
  });

  const networkErrorLink = createErrorLink(({ networkError, operation }) => {
    if (!networkError) return;

    onError(networkError, {
      metadata: { operationName: operation.operationName },
    });
  });

  const retryLink = new RetryLink(retry);
  const rawLinks = [
    retryLink,
    networkErrorLink,
    graphQLErrorLink,
    algoRequestIdTracker,
    logWeaselLink,
    costReportingLink,
  ];

  if (useGETForHashedQueries) {
    const persistedQueryLink = createPersistedQueryLink({
      sha256,
      useGETForHashedQueries: true,
    });

    rawLinks.push(persistedQueryLink);
  }

  rawLinks.push(httpLink);

  const links = from(rawLinks);

  const effectiveCache =
    cache ?? (new InMemoryCache() as unknown as ApolloCache<TCacheShape>);

  const client = new ApolloClient<TCacheShape>({
    cache: effectiveCache,
    name,
    version,
    typeDefs,
    link: links,
    defaultOptions,
  });

  const GraphQLApiProvider = ({ children }: { children: React.ReactNode }) => (
    <ApolloProvider client={client}>{children}</ApolloProvider>
  );

  GraphQLApiProvider.propTypes = {
    children: PropTypes.node.isRequired,
  };

  return {
    client,
    GraphQLApiProvider,
  };
};
